import React, { useEffect, useMemo, useState } from "react";
import { useRouter } from "next/router";
import { PlusIcon, EyeIcon } from "lucide-react";
import { ColumnDef } from "@tanstack/react-table";
import { useSelector } from "react-redux";
import { DateTime } from "luxon";
import EventDataTable from "../../banker/events/EventsDataTable";
import { getAssetTransferEvents } from "services";

interface ViewAuctionEventsProps {
  title?: string;
  showCreateButton?: boolean;
  onCreateClick?: () => void;
}

type AuctionEventRow = {
  uuid: string;
  listing_id: string;
  borrower_name: string;
  start_date_time: string;
  end_date_time: string;
  reserve_price: string;
  emd_amount: string;
  status: string;
};

function amountFormatter(amount: number): string {
  if (isNaN(amount)) return "0";
  return new Intl.NumberFormat("en-IN").format(amount);
}

const StatusMap: Record<string, string> = {
  draft: "DRAFT",
  pending: "PENDING",
  live: "LIVE AUCTION",
  end: "AUCTION ENDED",
  completed: "COMPLETED",
  cancelled: "CANCELLED",
  accepted: "ACCEPTED",
  pending_bid_approval: "PENDING BID APPROVAL",
  holding_bid_approval: "HOLDING BID APPROVAL",
  bid_rejected: "BID REJECTED",
  success: "SUCCESS",
  failed: "FAILED",
};

function mapAssetTransferRecordToRow(record: any): AuctionEventRow {
  const meta =
    record?.meta && typeof record.meta === "object" ? record.meta : {};
  const nested = meta?.asset_data || record?.asset_data || {};

  return {
    uuid: record?.uuid ?? "",
    listing_id:
      record?.event_id ??
      nested?.listing_id ??
      record?.listing_id ??
      record?.uuid ??
      "—",
    borrower_name:
      record?.title ??
      nested?.title ??
      record?.secured_asset_description ??
      nested?.secured_asset_description ??
      record?.borrower_name ??
      "Auction Event",
    start_date_time:
      record?.start_time ??
      nested?.start_time ??
      record?.bidding_start_at ??
      nested?.bidding_start_at ??
      "",
    end_date_time:
      record?.end_time ??
      nested?.end_time ??
      record?.bidding_end_at ??
      nested?.bidding_end_at ??
      "",
    reserve_price: String(
      record?.reserve_price ?? nested?.reserve_price ?? record?.sanction_amount ?? "",
    ),
    emd_amount: String(
      record?.emd_amount ?? nested?.emd_amount ?? "",
    ),
    status: record?.status ?? nested?.status ?? meta?.status ?? "draft",
  };
}

function teamRowsToFilterIdentifiers(teams: unknown): string[] {
  if (!Array.isArray(teams) || teams.length === 0) return [];
  const ids = new Set<string>();
  for (const t of teams as Record<string, unknown>[]) {
    if (t?.name) ids.add(String(t.name));
    else if (t?.slug) ids.add(String(t.slug));
    else if (t?.uuid) ids.add(String(t.uuid));
  }
  return Array.from(ids);
}

const columns: ColumnDef<AuctionEventRow>[] = [
  {
    id: "uuid",
    accessorKey: "uuid",
  },
  {
    accessorKey: "listing_id",
    header: "Event ID",
    cell: ({ row }) => (
      <div className="lowercase min-w-16">{row.original.listing_id}</div>
    ),
  },
  {
    accessorKey: "borrower_name",
    header: "Borrower Name",
    cell: ({ row }) => (
      <div className="font-medium min-w-[100px]">
        {(row.original.borrower_name as string)?.slice(0, 25) || "N/A"}
      </div>
    ),
  },
  {
    accessorKey: "start_date_time",
    header: "Start",
    cell: ({ row }) => (
      <div className="capitalize text-xs break-keep min-w-[200px]">
        {row.original.start_date_time ? (
          <div className="flex items-center divide-x divide-gray-400">
            <div className="pr-3 break-keep">
              {DateTime.fromISO(row.original.start_date_time)
                .setZone("Asia/Kolkata")
                .toFormat("dd MMM yyyy")}
            </div>
            <div className="px-3 text-xs break-keep">
              {`${DateTime.fromISO(row.original.start_date_time)
                .setZone("Asia/Kolkata")
                .toFormat("hh:mm a")} (IST)`}
            </div>
          </div>
        ) : null}
      </div>
    ),
  },
  {
    accessorKey: "end_date_time",
    header: "End",
    cell: ({ row }) => (
      <div className="capitalize text-xs break-keep min-w-[200px]">
        {row.original.end_date_time ? (
          <div className="flex items-center divide-x divide-gray-400">
            <div className="pr-3 break-keep">
              {DateTime.fromISO(row.original.end_date_time)
                .setZone("Asia/Kolkata")
                .toFormat("dd MMM yyyy")}
            </div>
            <div className="px-3 text-xs break-keep">
              {`${DateTime.fromISO(row.original.end_date_time)
                .setZone("Asia/Kolkata")
                .toFormat("hh:mm a")} (IST)`}
            </div>
          </div>
        ) : null}
      </div>
    ),
  },
  {
    accessorKey: "reserve_price",
    header: "Reserve price",
    cell: ({ row }) => {
      const amount = parseFloat(row.original.reserve_price);
      return <div className="min-w-20">₹{amountFormatter(amount)}</div>;
    },
  },
  {
    accessorKey: "status",
    header: "Status",
    cell: ({ row }) => (
      <div
        className={`capitalize px-2 py-1 rounded-full text-center text-xs font-normal
      ${
        row.original.status === "live"
          ? "bg-[#FF752C] text-white"
          : row.original.status === "pending_bid_approval"
          ? "bg-yellow-100 text-yellow-700"
          : row.original.status === "end"
          ? "bg-gray-100 text-gray-700"
          : [
              "cancelled",
              "failed",
              "bid_rejected",
              "holding_bid_approval",
            ].includes(row.original.status)
          ? "bg-red-50 text-red-500"
          : ["success"].includes(row.original.status)
          ? "bg-green-50 text-green-500"
          : row.original.status === "accepted"
          ? "bg-[#e0f2fe] text-[#075985]"
          : "bg-gray-200 text-gray-800"
      }
    `}
      >
        {row.original.status
          ? Object.keys(StatusMap).find(
              (key) =>
                StatusMap[key as keyof typeof StatusMap] ===
                row.original.status,
            ) || row.original.status
          : "N/A"}
      </div>
    ),
  },
  {
    accessorKey: "actions",
    header: "Actions",
  },
];

const SellerAuctionEvents: React.FC<ViewAuctionEventsProps> = ({
  title = "All Auction Events",
  showCreateButton = false,
  onCreateClick,
}) => {
  const router = useRouter();
  const teams = useSelector((state: any) => state.sellerAuth?.teams);
  const teamIdentifiers = useMemo(
    () => teamRowsToFilterIdentifiers(teams),
    [teams],
  );

  const [rows, setRows] = useState<AuctionEventRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      if (teamIdentifiers.length === 0) {
        setRows([]);
        setError(null);
        setLoading(false);
        return;
      }

      setLoading(true);
      setError(null);

      try {
        const res: any = await getAssetTransferEvents({
          teamIdentifiers,
          page: 1,
          perPage: 10,
        });
        const raw = Array.isArray(res?.data) ? res.data : res?.data?.data ?? [];
        const list = Array.isArray(raw) ? raw : [];
        if (!cancelled) {
          setRows(list.map(mapAssetTransferRecordToRow));
        }
      } catch (e) {
        console.error("getAssetTransferEvents failed", e);
        if (!cancelled) {
          setRows([]);
          setError("Could not load auction events. Please try again.");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [teamIdentifiers]);

  return (
    <div className="px-4 lg:px-8 py-6">
      <div className="flex justify-between items-center">
        <h1 className="text-lg font-openSans font-normal text-bankerColor">
          {title}
        </h1>
        {showCreateButton && (
          <button
            type="button"
            onClick={
              onCreateClick ?? (() => {
                router.push("/auction-events/create");
              })
            }
            className="flex justify-center items-center w-[140px] gap-1 bg-black text-white text-sm px-2 py-3 rounded-[12px]"
          >
            <PlusIcon />
            Create Event
          </button>
        )}
      </div>
      {teamIdentifiers.length === 0 && !loading && (
        <p className="mt-3 text-sm text-[#777]">
          No team auction events linked to your account. Sign in again after team access
          is assigned.
        </p>
      )}
      {error && (
        <p className="mt-3 text-sm text-red-600" role="alert">
          {error}
        </p>
      )}
      <div className="mt-4 border-2 border-gray-100 rounded-xl bg-white">
        <EventDataTable<AuctionEventRow>
          data={rows}
          columns={columns}
          loading={loading}
          actions={(row) => (
            <button
              onClick={() => router.push(`/auction-events/${row.uuid}`)}
              className="text-xs text-[#f5761a] hover:underline"
            >
              <EyeIcon className="w-4 h-4" />
            </button>
          )}
        />
      </div>
    </div>
  );
};

export default SellerAuctionEvents;